import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Col, Container, media, Row, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {InViewAnimContainer} from 'UI/components/layout/InViewAnimContainer';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H3, H6} from 'UI/components/layout/Text';

import {ImageViewAnimation} from 'UI/animations/images';
import {FadeInAnimation} from 'UI/animations/shared';

import {useFeaturesBig} from './useFeaturesBig';
import {transient} from 'Application/configs/emotionTransientProps';
import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';

import type {FeaturesBigBlock} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object expected by the `FeaturesBig` component.
 * It includes optional content fields for the feature's imagery and text as well as required identifiers for testing and theming.
 * This interface leverages the `FeaturesBigBlock` type from the Payload CMS schema to ensure type consistency between the CMS data and the component.
 */
interface ComponentProps {
    /**
     * The optional cover image boolean flag indicating whether the feature image should be displayed as a cover image.
     * This property is used to conditionally apply styling and layout adjustments for cover images.
     *
     * @default false
     */
    cover?: FeaturesBigBlock['cover'];
    /**
     * The optional rich text description content rendered below the headline and subline using the Payload Lexical editor format.
     */
    description?: FeaturesBigBlock['description'];
    /**
     * The optional icon image upload displayed as a circular overlay on the bottom-left corner of the feature image.
     */
    featuresBigIcon?: FeaturesBigBlock['featuresBigIcon'];
    /**
     * The optional main feature image displayed prominently at the top of the block in a responsive aspect ratio.
     */
    featuresBigImage?: FeaturesBigBlock['featuresBigImage'];
    /**
     * The optional headline text rendered above the subline as a smaller label-style heading.
     */
    headline?: FeaturesBigBlock['headline'];
    /**
     * The optional subline text that may contain line break markers, rendered as the primary heading below the headline.
     */
    subline?: FeaturesBigBlock['subline'];
    /**
     * A unique identifier assigned to the component for testing purposes, enabling reliable element targeting in automated tests.
     */
    testId: string;
    /**
     * The theme data attribute applied to the component's container for switching between color schemes and styling contexts.
     */
    theme: FeaturesBigBlock['theme'];
}

/**
 * The `FeaturesBig` component renders a responsive feature highlight section with a large image, decorative icon overlay, and structured textual content.
 * It applies scroll-triggered motion animations to the image, headline, and subline elements for a dynamic entry effect as they come into view.
 * The component uses a responsive grid layout that adapts across breakpoints and integrates theme-aware color styling via the `data-nfq-theme` attribute.
 *
 * @param props                  The component props.
 * @param props.description      The optional rich text description content rendered using the Payload Lexical editor format.
 * @param props.featuresBigIcon  The optional icon image upload displayed as a circular overlay on the feature image.
 * @param props.featuresBigImage The optional main feature image displayed prominently at the top of the block.
 * @param props.headline         The optional headline text rendered as a smaller label-style heading above the subline.
 * @param props.subline          The optional subline text supporting line break markers, rendered as the primary heading.
 * @param props.testId           A unique identifier for targeting the component in automated tests, defaulting to `'FeaturesBig'`.
 * @param props.theme            The theme data attribute for switching between color schemes on the component container.
 * @returns A JSX element representing the feature highlight section with animated imagery and text content.
 *
 * @example
 * ```tsx
 * <FeaturesBig
 *   description={lexicalContent}
 *   featuresBigIcon={iconUpload}
 *   featuresBigImage={imageUpload}
 *   headline="Intuitive Design"
 *   subline="Designed for clarity\nand ease of use"
 *   testId="feature-section"
 *   theme="dark"
 * />
 * ```
 */
const FeaturesBig = observer(({
    cover = false,
    description,
    featuresBigIcon,
    featuresBigImage,
    headline,
    subline,
    testId = 'FeaturesBig',
    theme
}: ComponentProps) => {
    const {
        colors,
        iconAlt,
        iconHeight,
        iconUrl,
        iconWidth,
        sublineRows
    } = useFeaturesBig(featuresBigIcon, subline);

    return (
        <Wrapper data-nfq-theme={theme}>
            <StyledContainer data-cy={testId}>
                <Row>
                    <RelativeCol
                        offset={{
                            xs: 0, xxl: 1
                        }}
                        xs={12}
                        xxl={10}
                    >
                        <StyledPicture
                            $coverImage={cover ?? false}
                            $height={
                                isDefinedAndPopulated(featuresBigImage?.image)
                                    ? featuresBigImage.image.height ?? undefined : undefined
                            }
                            $width={
                                isDefinedAndPopulated(featuresBigImage?.image)
                                    ? featuresBigImage.image.width ?? undefined : undefined
                            }
                            image={featuresBigImage}
                            initial="outOfView"
                            transition={{
                                duration: 0.5,
                                ease: 'easeInOut'
                            }}
                            variants={ImageViewAnimation}
                            viewport={{margin: '0px 0px -20% 0px'}}
                            whileInView="inView"
                            isLazy
                            isPresentational
                        />
                        {iconUrl && (
                            <IconWrap>
                                <StyledIcon>
                                    <svg aria-label={iconAlt} viewBox={`0 0 ${iconWidth} ${iconHeight}`}>
                                        <use height={iconHeight ?? 'auto'} href={`${iconUrl}#Icon`} width={iconWidth ?? 'auto'} />
                                    </svg>
                                </StyledIcon>
                            </IconWrap>
                        )}
                    </RelativeCol>
                </Row>
                <Spacer
                    y={{
                        xs: 8, lg: 16
                    }}
                    isNotStretching
                />
                <InViewAnimContainer baseComponent={Row}>
                    <Col
                        lg={6}
                        offset={{
                            xs: 0, xxl: 1
                        }}
                        xs={12}
                        xxl={5}
                    >
                        <MotionH6 variants={FadeInAnimation}>
                            <Color $color={colors.secondary}>
                                {headline}
                            </Color>
                        </MotionH6>
                        <Spacer y={2} isNotStretching />
                        <MotionH3 variants={FadeInAnimation}>
                            {...sublineRows}
                        </MotionH3>
                    </Col>
                    <ColoredCol lg={4} xs={12} xxl={3}>
                        <motion.div variants={FadeInAnimation}>
                            {description && <PayloadLexical description={description} />}
                        </motion.div>
                    </ColoredCol>
                </InViewAnimContainer>
            </StyledContainer>
        </Wrapper>
    );
});

FeaturesBig.displayName = 'FeaturesBig';

export {FeaturesBig};

const Wrapper = styled.div`
    background-color: ${({theme}) => theme.colors.bgPrimary};
`;

const StyledContainer = styled(Container)`
    padding-block: ${spacing(14)};

    ${media('md')} {
        padding-block: ${spacing(20)};
    }
`;

const RelativeCol = styled(Col)`
    position: relative;
`;

interface StyledPictureProps {
    $coverImage: boolean;
    $height?: number;
    $width?: number;
}

const StyledPicture = styled(motion.create(PayloadImage), {...transient})<StyledPictureProps>`
    aspect-ratio: ${({$coverImage, $height, $width}) => ($coverImage ? '540 / 675' : `${$width} / ${$height}`)};
    object-fit: ${({$coverImage}) => ($coverImage ? 'cover' : 'contain')};
    width: 100%;

    ${media('sm')} {
        aspect-ratio: ${({$coverImage, $height, $width}) => ($coverImage ? '780 / 780' : `${$width} / ${$height}`)};
    }
    ${media('md')} {
        aspect-ratio: ${({$coverImage, $height, $width}) => ($coverImage ? '960 / 600' : `${$width} / ${$height}`)};
    }
    ${media('lg')} {
        aspect-ratio: ${({$coverImage, $height, $width}) => ($coverImage ? '1200 / 725' : `${$width} / ${$height}`)};
    }
    ${media('xl')} {
        aspect-ratio: ${({$coverImage, $height, $width}) => ($coverImage ? '1500 / 900' : `${$width} / ${$height}`)};
    }
`;

const IconWrap = styled.div`
    align-items: center;
    border: 2px solid ${({theme}) => theme.colors.bgPrimary};
    border-radius: 50%;
    bottom: 2rem;
    display: flex;
    height: 4.8rem;
    justify-content: center;
    left: 2rem;
    position: absolute;
    width: 4.8rem;

    ${media('lg')} {
        bottom: 3rem;
        height: 6rem;
        left: 3rem;
        width: 6rem;
    }
`;

const StyledIcon = styled.div`
    color: ${({theme}) => theme.colors.bgPrimary};
    height: 1.7rem;
    width: 1.7rem;

    ${media('lg')} {
        height: 3.2rem;
        width: 3.2rem;
    }
`;

const H6P = H6.withComponent('p');

const H3H2 = H3.withComponent('h2');

const MotionH6 = styled(motion.create(H6P), {...transient})``;

const MotionH3 = styled(motion.create(H3H2), {...transient})`
    color: ${({theme}) => theme.colors.primary};
`;

const ColoredCol = styled(Col)`
    color: ${({theme}) => theme.colors.secondary};
`;